import type { Seconds } from '@lemonade-hq/ts-helpers';
import { cubicBezier, motion } from 'motion/react';
import { forwardRef } from 'react';
import { foundations } from '../../styles/foundations.css';
import { spacing } from '../../styles/spacing.css';
import type { LayoutProps } from '../Layout/Layout';
import { Layout } from '../Layout/Layout';
import { progressBarInner } from './ProgressBar.css';

export interface ProgressBarProps extends Omit<LayoutProps<typeof motion.div>, 'as' | 'children'> {
  readonly progress: number;
  readonly customAnimation?: boolean;
  readonly disableInitialAnimation?: boolean;
  readonly progressColor?: 'brand' | 'dark';
}

const CUBIC_BEZIER_ANIMATION_DURATION: Seconds = 0.5;
const cubicBezierTransition = {
  duration: CUBIC_BEZIER_ANIMATION_DURATION,
  ease: cubicBezier(0.12, 0.02, 0.17, 0.99),
};

const baseStyle = foundations({ borderRadius: 'sm' });

export const getPercentage = (progress: number): number => Math.min(Math.max(progress, 0), 1) * 100;

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      progress,
      backgroundColor = '$progressBarBackground',
      customAnimation = false,
      disableInitialAnimation = false,
      progressColor = 'dark',
      ...props
    },
    ref,
  ) => {
    const percentage = getPercentage(progress);

    const progressBarAnimationProps = customAnimation
      ? { ref, initial: { width: 0 } }
      : {
          initial: disableInitialAnimation ? false : { width: 0 },
          transition: cubicBezierTransition,
          animate: { width: `${percentage}%` },
        };

    return (
      <Layout
        {...props}
        aria-label="progress"
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={percentage}
        as={motion.div}
        backgroundColor={backgroundColor}
        className={baseStyle}
        height={spacing.s06}
        layout
        role="progressbar"
      >
        <Layout
          as={motion.div}
          className={`${baseStyle} ${progressBarInner({ progressColor })}`}
          height="inherit"
          layout
          role="presentation"
          {...progressBarAnimationProps}
        />
      </Layout>
    );
  },
);
